import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectSelectionMode, selectSelectedIds,
  exitSelectionMode, setSelectedIds, toggleSelection,
} from '../redux/features/context/contextSlice';

const DEFAULT_GET_ID = (entity) => entity.id;

/**
 * Entity-agnostic batch-selection controller for a paginated card grid.
 *
 * Wraps the selection state in contextSlice and derives everything a view needs
 * to drive a SelectionBar and per-card actions, given the current page's
 * entities. Reusable across entities — the only per-entity input is how to read
 * an id (`getId`, defaults to `entity.id`).
 *
 * Note: the grid is server-paginated, so "select all" is scoped to the loaded
 * page. `selectAllLabel` reflects that.
 *
 * @param {Array} pageEntities entities currently rendered on the page
 * @param {{ getId?: (entity: any) => string }} [options]
 */
export default function useBatchSelection(pageEntities = [], options = {}) {
  const { getId = DEFAULT_GET_ID } = options;
  const dispatch = useDispatch();

  const selectionMode = useSelector(selectSelectionMode);
  const selectedIds = useSelector(selectSelectedIds);

  const pageIds = useMemo(() => pageEntities.map(getId), [pageEntities, getId]);
  const selectedIdSet = useMemo(() => new Set(selectedIds), [selectedIds]);

  const selectedEntities = useMemo(
    () => pageEntities.filter((e) => selectedIdSet.has(getId(e))),
    [pageEntities, selectedIdSet, getId],
  );

  const allPageSelected = pageIds.length > 0 && pageIds.every((id) => selectedIdSet.has(id));
  const selectAllLabel = allPageSelected ? 'Deselect all' : `Select all (${pageIds.length})`;

  const isSelected = useCallback((id) => selectedIdSet.has(id), [selectedIdSet]);
  const toggle = useCallback((id) => dispatch(toggleSelection(id)), [dispatch]);

  const selectPage = useCallback(() => {
    dispatch(setSelectedIds(Array.from(new Set([...selectedIds, ...pageIds]))));
  }, [dispatch, selectedIds, pageIds]);

  const selectAll = useCallback(() => {
    dispatch(setSelectedIds(allPageSelected ? [] : pageIds));
  }, [dispatch, allPageSelected, pageIds]);

  const clear = useCallback(() => dispatch(exitSelectionMode()), [dispatch]);

  return {
    selectionMode,
    selectedIds,
    selectedCount: selectedIds.length,
    selectedEntities,
    pageIds,
    allPageSelected,
    selectAllLabel,
    isSelected,
    toggle,
    selectPage,
    selectAll,
    clear,
  };
}
